import { useOktaAuth } from "@okta/okta-react";
import { Space } from "../../services/coworkerApiCalls/Space/SpacesTypes";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faQuestionCircle, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { createSpace, deleteSpace, updateSpace } from "../../services/coworkerApiCalls/Space/Space";
import { ChangeEvent, useEffect, useState } from "react";
import { Quota, Quotas } from "../../services/coworkerApiCalls/Quotas/QuotasTypes";
import { useDataApiServices } from "../../Hooks/useDataServicesApi";
import { Tooltip } from 'react-tooltip';
import { useDataApiArticlesByCategory } from "../../Hooks/useDataArticlesApi";

type SpaceCrudProperties = Space;

// Définition du type SpaceData
type SpaceData = {
  id: number;
  name: string;
  city: string;
  address: string;
  postCode: number;
  charterId: number | undefined;
  informationId: number | undefined;
  quotas: Quotas;
};

type DataItem = {
  departmentId: number;
  quotaValue: number;
}

// eslint-disable-next-line unicorn/consistent-function-scoping
const handleNavigation = (path: string) => {
  window.location.href = path; // Change l'URL et recharge la page
};


const delayNavigation = (path: string, delay: number) => {
  setTimeout(() => {
    handleNavigation(path);
  }, delay);
};

function onDelete(token: string, id: number) {
  const userConfirmed = confirm("Êtes vous sur de vouloir supprimer ?")
  if (userConfirmed) {
    // L'utilisateur a confirmé, appeler la fonction deleteSpace
    void deleteSpace(token, id);
    delayNavigation("/spaces", 2000);
  }
}

const SpaceCrud: React.FC<SpaceCrudProperties> = ({ id,name, city, postCode, quotas}) => {

  const { authState } = useOktaAuth();

  const idToken = authState!.idToken!.idToken 

  let quotaTotal = 0;

  let quotaTooltip = "Quota par service : <br>";

  for(const quota of quotas){
    quotaTotal += quota.value;
    quotaTooltip += " • " + quota.departmentName + " : " + quota.value.toString() + "<br>";  
  }
  if(quotaTotal == 0){
    quotaTooltip = "Aucun service n'est relié à cet espace"
  }
  return (

    <tr className="border-b border-gray-200">
        <td className="p-4"><strong className="text-gray-500"> {name} </strong></td>
        <td className="p-4">{postCode}</td>
        <td className="p-4">{city}</td>
        <td className="p-4">
          <Tooltip id="my-tooltip-multiline"/>
          <a data-tooltip-id="my-tooltip-multiline" data-tooltip-html={quotaTooltip} data-tooltip-place="top">
            <span className="tooltip-icon">
              {quotaTotal} <FontAwesomeIcon icon={faQuestionCircle} className="question-mark" />
            </span>
          </a>
        </td>
        <td className="p-4 flex justify-end items-center">
            <Link to={`/edit-space/${id}`}>
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
              ><FontAwesomeIcon icon={faEdit} /></button>
            </Link>

            <button
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              onClick={() => onDelete(idToken, id)}
            ><FontAwesomeIcon icon={faTrashAlt} /></button>
        </td>
    </tr>
  )
}

const SpaceEdit: React.FC<Space> = ({id, name, city, address, postCode, charterId, informationId, quotas}) => {

  const [idEdit] = useState(id)
  const [nameEdit, setNameEdit] = useState(name)
  const [cityEdit, setCityEdit] = useState(city)
  const [addressEdit, setAddressEdit] = useState(address)
  const [postCodeEdit, setPostCodeEdit] = useState(postCode)
  const [charterIdEdit, setCharterIdEdit] = useState(charterId)
  const [informationIdEdit, setInformationIdEdit] = useState(informationId)
  const [quotasEdit] = useState<Quotas>([]);
  
  const allServices = useDataApiServices();
  const allCharter = useDataApiArticlesByCategory(5);
  const allInformation = useDataApiArticlesByCategory(3);
  
  const { authState } = useOktaAuth();
  const idToken = authState!.idToken!.idToken;
  

  const [data, setData] = useState<DataItem[]>([{ departmentId: 0, quotaValue: 0 }]);

  // add a new line in the form to attribute a new service and quota for the space
  const handleClick = () => {
    if(allServices && data.length != allServices.length){
      setData([...data, {departmentId:0, quotaValue:0}]);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLSelectElement | HTMLInputElement>, index: number) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { name, value } = event.target;
    const onChangeValue = [...data];
    if (name === 'departmentId') {
      onChangeValue[index].departmentId = Number.parseInt(value);
    } 
    else if (name === 'quotas') {
      onChangeValue[index].quotaValue = Number.parseInt(value);
    }
    setData(onChangeValue);
  };

  // To delete one service of the quota part
  const handleDelete = (index: number) => {
    const deleteValue = [...data];
    deleteValue.splice(index,1);
    setData(deleteValue);
  };

  const countServices = (services: DataItem[], departmentId : number) => {
    let count = 0
    for (const service of services) {
      if(service.departmentId == departmentId){
        count++
      }
    }
    return count;
  }

  // To know if a service in the previous list data has a departmentId and quotaValue == 0 or ""
  // it's use to know if informations of quota part are correct so we can print the button or not
  const isServiceNull = (services: DataItem[]) => {
    if(services.length === 0 || services == undefined){
      return false;
    }
    for (const service of services) {

      if(countServices(services,service.departmentId) > 1){
        return false
      }
      if (service.departmentId === 0 || service.quotaValue === 0) {
        return false;
      }
    }
    return true;
  };

  // When we want to modify a space, this function allows you to initialize tab data with quotas from the space one time or each time id or quotas change 
  useEffect(() => {
    if(id){
      const initialData = quotas.map(quota => ({
          departmentId: quota.departmentId,
          quotaValue: quota.value,
      }));
      setData(initialData);
    }
  }, [id, quotas]);

  // To submit the form 
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    data.map(quota =>{
      // Construction of the object Quota
      const newQuota: Quota = {
        departmentId: quota.departmentId, 
        spaceId: id, 
        value: quota.quotaValue,
        departmentName: "Default Department Name"
      };
      quotasEdit.push(newQuota);
    })
    
    const spaceData: SpaceData = {
      id: idEdit, 
      name: nameEdit,
      city: cityEdit,
      address: addressEdit,
      postCode: postCodeEdit,
      charterId: charterIdEdit,
      informationId: informationIdEdit,
      quotas: quotasEdit
    };

    if (idEdit === 0) {
      void createSpace(idToken, spaceData)
      delayNavigation("/spaces", 2000);
    } else {
      void updateSpace(idToken, spaceData)
      delayNavigation("/spaces", 2000);
    }

    // Ici, vous pouvez gérer la logique pour ajouter l'article (par exemple, envoi à une API)
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNameEdit(event.target.value);
  };
  const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCityEdit(event.target.value);
  };
  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddressEdit(event.target.value);
  };
  const handlePostCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPostCodeEdit(Number.parseInt(event.target.value));
  };
  const handleCharterIdChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCharterIdEdit(Number.parseInt(event.target.value));
  };
  const handleInformationIdChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setInformationIdEdit(Number.parseInt(event.target.value));
  };

  return (
    <form onSubmit={handleSubmit} action="/add-space" method="post" className="flex flex-col gap-6 bg-white shadow-lg rounded-lg p-8">
      <div className="flex justify-between items-center mb-5"> 
        <h2 className="text-2xl font-semibold">
          {id ? 
            <>Modifier </> 
            : 
            <>Ajouter </>
          } 
          un espace
        </h2>
        {nameEdit != "" && addressEdit != "" && cityEdit != "" && postCodeEdit != 0 && isServiceNull(data) &&( 
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            {id ? 
              <>Modifier </> 
              : 
              <>Ajouter </>
            } 
            l'espace
          </button>
         )}
      </div>
      <div className="form-group">
        <label htmlFor="name" className="text-lg font-semibold mb-2">Nom</label>
        <input
          type="text"
          name="name"
          value={nameEdit}
          onChange={handleNameChange}
          placeholder="Nom"
          className="mt-1 mb-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
          required
        />
        <label htmlFor="address" className="text-lg font-semibold mb-2">Adresse</label>
        <input
          type="text"
          name="address"
          value={addressEdit}
          onChange={handleAddressChange}
          placeholder="Adresse"
          className="mt-1 mb-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
          required
        />
        <div className="flex space-x-4 mb-3"> 
          <div className="flex-1"> 
            <label htmlFor="postCode" className="block text-lg font-semibold mb-2">Code postal</label>
            <input
              type="text"
              name="postCode"
              value={postCodeEdit}
              onChange={handlePostCodeChange}
              placeholder="Code postal"
              className="mt-1 mb-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
              required
            />
          </div>
          <div className="flex-1">
            <label htmlFor="City" className="block text-lg font-semibold mb-2">Ville</label>
            <input
              type="text"
              name="City"
              value={cityEdit}
              onChange={handleCityChange}
              placeholder="Ville"
              className="mt-1 mb-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
              required
            />
          </div>
        </div>
        {allCharter && (
          <div className="form-group w-full md:w-1/2 mb-3">
            <label htmlFor="categoryId" className="text-lg font-semibold mb-2">Choix de la charte de coworking :</label>
            <select
              name="charterId"
              value={charterIdEdit || ''}
              onChange={handleCharterIdChange}
              className="mt-1 mb-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
            >
              <option value="">Choisir la charte</option>
              {allCharter.map(charter => (
                <option key={charter.id} value={charter.id}>
                  {charter.title}
                </option>
              ))}
              {allCharter.length === 0 && (
                <option value="" disabled>Aucune charte disponible</option>
              )}
            </select>
          </div>
        )}
        {allInformation && (
          <div className="form-group w-full md:w-1/2 mt-1 mb-3">
            <label htmlFor="informationId" className="text-lg font-semibold mb-2">Choix de la rubrique information :</label>
            <select
              name="informationId"
              value={informationIdEdit || ''}
              onChange={handleInformationIdChange}
              className="mt-1 mb-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
            >
              <option value="">Choisir la rubrique information</option>
              {allInformation.map(information => (
                <option key={information.id} value={information.id}>
                  {information.title}
                </option>
              ))}
              {allInformation.length === 0 && (
                <option value="" disabled>Aucune rubrique disponible</option>
              )}
            </select>
          </div>
        )}
        <label htmlFor="Quotas" className="text-lg font-semibold mb-2">Quotas :</label>
        <br />
        <button type="button" className="rounded mt-2 mb-4 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4" onClick={handleClick}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" /> Ajouter un Service
        </button>

        <table className="w-full table-auto">
          <thead>
              <tr className="text-left">
                  <th className="p-4">Service</th>
                  <th className="p-4">Quota</th>
                  <th className="p-4"></th>
              </tr>
          </thead>
          <tbody className="w-full">
              {
                data.map((value, index) => (
                  <tr key={index}>
                      <td className="p-2">
                          {allServices && (
                              <select
                                  name="departmentId"
                                  value={value.departmentId || 0}
                                  onChange={(event) => handleChange(event, index)}
                                  className="mt-1  block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                                  required
                              >
                                  <option value={0}>Choisir le service</option>
                                  {allServices.map((service) => (
                                      <option key={service.id} value={service.id}>
                                          {service.name}
                                      </option>
                                  ))}
                                  {allServices.length === 0 && (
                                      <option value="" disabled>
                                          Aucun service disponible
                                      </option>
                                  )}
                              </select>
                          )}
                      </td>
                      <td className="p-2">
                          <input
                              type="number"
                              name="quotas"
                              min="1" max="100"
                              value={value.quotaValue}
                              onChange={(event) => handleChange(event, index)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                              required
                          />
                      </td>
                      <td className="p-2">
                          <button
                              type="button"
                              className="rounded mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4"
                              onClick={() => handleDelete(index)}
                          >
                              Supprimer
                          </button>
                      </td>
                  </tr>
              ))}
          </tbody>
        </table>
      </div>
    </form>
  );
};

export {SpaceCrud, SpaceEdit };

